import React, { useLayoutEffect } from 'react';
import useBundleTranslation from 'i18n';
import { Box, Stack } from '@mui/material';
import { YNValues } from 'tools/types';
import { FormDataAPIType } from 'components/common/form';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import EditPanelLabel from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelLabel';
import {
    DatasetField,
    KpiAllowedMeasureFieldAggregations,
    MeasureField,
    VarianceCalcMethodType,
} from '@report-content';
import ReactSelect from 'components/common/react-select/ReactSelect';

export default function KpiVarianceSection({
    elementProps,
    varianceTarget,
    fieldsList,
    onTargetUpdate,
}: {
    elementProps: FormDataAPIType;
    varianceTarget: MeasureField;
    fieldsList: DatasetField[];
    onTargetUpdate: (target: MeasureField) => void;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const includeKpiVariance: YNValues = elementProps.form.hookFormWatch('includeKpiVariance') ?? 'N';
    const varianceCalcMethod: VarianceCalcMethodType =
        elementProps.form.hookFormWatch('varianceCalcMethod') ?? 'prior values';

    const allowedAggregations = KpiAllowedMeasureFieldAggregations.filter(
        (f) =>
            f.value_type == varianceTarget.value_type &&
            !((f.name == 'Min' || f.name == 'Max') && varianceTarget.value_type == 'datetime'),
    );

    useLayoutEffect(() => {
        if (!varianceTarget.reference_name) {
            return;
        }
        if (!allowedAggregations.map((a) => a.name).includes(varianceTarget.aggregation_function)) {
            onTargetUpdate({ ...varianceTarget, aggregation_function: allowedAggregations[0].name });
        }
    }, [allowedAggregations, varianceTarget.reference_name]);

    return (
        <Stack spacing={2}>
            <EditPanelCheckBox
                elementProps={elementProps}
                name={'includeKpiVariance'}
                label={t('kpi_chart.include_kpi_variance')}
            />
            <Stack spacing={2} className={includeKpiVariance == 'Y' ? '' : 'd-none'}>
                <Box>
                    <EditPanelLabel>{t('kpi_chart.variance_calc_method')}</EditPanelLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormSelect'}
                        name={'varianceCalcMethod'}
                        componentValues={[
                            { label: t('kpi_chart.variance_calc_method_prior_value'), value: 'prior values' },
                            { label: t('kpi_chart.variance_calc_method_target'), value: 'target comparison' },
                        ]}
                    />
                </Box>
                <Box className={varianceCalcMethod == 'target comparison' ? '' : 'd-none'}>
                    <EditPanelLabel>{t('kpi_chart.variance_target')}</EditPanelLabel>
                    <Stack direction={'row'} spacing={1}>
                        <Box sx={{ width: '100%' }} data-test={`measure-select-aggregation-variance-target`}>
                            <ReactSelect
                                data={
                                    varianceTarget.reference_name
                                        ? allowedAggregations.map((f) => ({
                                              label: f.label,
                                              value: f.name,
                                          }))
                                        : [{ label: '--', value: '' }]
                                }
                                value={varianceTarget.aggregation_function}
                                update={(value) => onTargetUpdate({ ...varianceTarget, aggregation_function: value })}
                            />
                        </Box>
                        <Box sx={{ alignSelf: 'center' }}>{t('of')}</Box>
                        <Box sx={{ width: '100%' }} data-test={`measure-select-reference-variance-target`}>
                            <ReactSelect
                                data={[
                                    { label: '--', value: '' },
                                    ...fieldsList.map((f) => ({
                                        label: f.column_name,
                                        value: f.reference_name,
                                    })),
                                ]}
                                value={varianceTarget.reference_name}
                                update={(value) => {
                                    if (!value) {
                                        onTargetUpdate({
                                            ...varianceTarget,
                                            reference_name: '',
                                            column_name: '',
                                            value_type: 'text',
                                        });
                                        return;
                                    }
                                    const f = fieldsList.find((f) => f.reference_name == value);
                                    if (!f) {
                                        return;
                                    }
                                    onTargetUpdate({
                                        ...varianceTarget,
                                        reference_name: f.reference_name,
                                        column_name: f.column_name,
                                        value_type: f.value_type,
                                    });
                                }}
                            />
                        </Box>
                    </Stack>
                </Box>
                <Stack className={varianceCalcMethod == 'prior values' ? '' : 'd-none'} direction={'row'} spacing={1}>
                    <Box sx={{ width: '167px' }}>
                        <EditPanelLabel>{t('kpi_chart.compare_value_period')}</EditPanelLabel>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormText'}
                            name={'varianceCompareValuePeriod'}
                            label={''}
                        />
                    </Box>
                    <Box sx={{ width: '100px' }}>
                        <EditPanelLabel>{t('kpi_chart.compare_value_unit')}</EditPanelLabel>
                        <TemplateFormComponent
                            elementProps={elementProps}
                            component={'FormSelect'}
                            name={'varianceCompareValueUnit'}
                            componentValues={[
                                { label: 'day', value: 'day' },
                                { label: 'week', value: 'week' },
                                { label: 'month', value: 'month' },
                                { label: 'year', value: 'year' },
                            ]}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', pl: '4px', pt: '18px' }} alignItems={'center'}>
                        {t('kpi_chart.ago')}
                    </Box>
                </Stack>
                <Box>
                    <EditPanelLabel>{t('kpi_chart.show_variance_as')}</EditPanelLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormToggleButtonGroup'}
                        name={'showVarianceAs'}
                        props={{ width: '100%' }}
                        componentValues={[
                            { label: t('kpi_chart.percentage_value'), value: 'percentage' },
                            { label: t('kpi_chart.absolute_value'), value: 'absolute' },
                        ]}
                    />
                </Box>
            </Stack>
        </Stack>
    );
}
